import { type IconDefinition } from '@/components/ui/app-icon';
import {
  iconArrowsRotate,
  iconBriefcase,
  iconBuildings,
  iconCopy,
  iconCalendarClock,
  iconCalendarDays,
  iconDatabase,
  iconFileLines,
  iconGlobe,
  iconListCheck,
  iconTable,
  iconUserGroup,
  iconWebhook,
} from '@/lib/icons';
import {
  isCalendarTriggerKind,
  isNotionTriggerKind,
  triggerKind,
  type IncomingWebhookConfig,
  type PlayCrm,
  type TriggerConfig,
  type TriggerKind,
} from '@/types/custom-playbooks';
import type { ConnectionSource, ConnectorId } from './connectors';
import { BRAND } from '@/lib/brand';

/**
 * The trigger rows the picker offers, in the order it shows them. A leaf
 * module so both the picker and the inspector's trigger body read the same
 * list without importing a component from each other.
 */

export type TriggerCategory = 'When something changes' | 'On a schedule';

export interface TriggerRow {
  key: TriggerKind;
  category: TriggerCategory;
  icon: IconDefinition;
  iconColor: string;
  name: string;
  description: string;
}

const CHANGE = '#F59E0B';
const SCHEDULE = '#3E8BFF';

export const TRIGGER_ROWS: TriggerRow[] = [
  {
    key: 'deal_stage',
    category: 'When something changes',
    icon: iconArrowsRotate,
    iconColor: CHANGE,
    name: 'A deal changes stage',
    description: 'An opportunity or deal enters one of your chosen stages',
  },
  {
    key: 'deal_stalled',
    category: 'On a schedule',
    icon: iconBriefcase,
    iconColor: SCHEDULE,
    name: 'A deal stalls',
    description:
      'An open deal has no activity for the number of days you choose',
  },
  {
    key: 'sixsense_stage',
    category: 'When something changes',
    icon: iconBuildings,
    iconColor: CHANGE,
    name: 'An account reaches a 6sense stage',
    description: 'A Salesforce account enters a 6sense buying stage you choose',
  },
  ...(['booked', 'starting', 'no_notes'] as const).map(
    (event): TriggerRow => ({
      key: `meeting_${event}`,
      category: 'When something changes',
      icon: iconCalendarDays,
      iconColor: CHANGE,
      name:
        event === 'booked'
          ? 'A Google Calendar meeting is booked'
          : event === 'starting'
            ? 'A Google Calendar meeting is starting'
            : 'A Google Calendar meeting has no notes',
      description:
        event === 'booked'
          ? 'Outside attendees of a new meeting on Google Calendar'
          : event === 'starting'
            ? 'Outside attendees before a Google Calendar meeting starts'
            : 'Google Calendar meetings with no notes two hours after they end',
    })
  ),
  {
    key: 'fireflies_call',
    category: 'When something changes',
    icon: iconCalendarClock,
    iconColor: CHANGE,
    name: 'A Fireflies call is recorded',
    description: 'Start with outside participants when Fireflies notes sync',
  },
  {
    key: 'fathom_call',
    category: 'When something changes',
    icon: iconCalendarClock,
    iconColor: CHANGE,
    name: 'A Fathom call is recorded',
    description: 'Start with outside participants when Fathom notes sync',
  },
  {
    key: 'gong_call',
    category: 'When something changes',
    icon: iconCalendarClock,
    iconColor: CHANGE,
    name: 'A Gong call is recorded',
    description: 'Start with outside participants when Gong notes sync',
  },
  {
    key: 'sequence_event',
    category: 'When something changes',
    icon: iconArrowsRotate,
    iconColor: CHANGE,
    name: 'Something happens in a sequence',
    description: `Run when a person replies, finishes or reaches a step in a ${BRAND.name} sequence`,
  },
  {
    key: 'play_handoff',
    category: 'When something changes',
    icon: iconArrowsRotate,
    iconColor: CHANGE,
    name: 'Another play sends records',
    description: 'Receive people or companies from another play of this agent',
  },
  {
    key: 'webhook',
    category: 'When something changes',
    icon: iconArrowsRotate,
    iconColor: CHANGE,
    name: 'A webhook arrives',
    description: 'Receive a form or JSON submission from another app',
  },
  {
    key: 'crm_property_change',
    category: 'When something changes',
    icon: iconArrowsRotate,
    iconColor: CHANGE,
    name: 'A record matches your conditions',
    description: 'A field on an account, contact, or lead starts matching',
  },
  {
    key: 'crm_list_membership',
    category: 'When something changes',
    icon: iconListCheck,
    iconColor: CHANGE,
    name: 'A record joins a list',
    description: 'Someone lands in a HubSpot list or a Salesforce campaign',
  },
  {
    key: 'list_leads_added',
    category: 'When something changes',
    icon: iconListCheck,
    iconColor: CHANGE,
    name: 'Leads added to a list',
    description: `Run on leads added to one of your ${BRAND.name} lists`,
  },
  {
    key: 'sheet_rows_added',
    category: 'When something changes',
    icon: iconTable,
    iconColor: CHANGE,
    name: 'Rows are added to a Google Sheet',
    description: 'Run on each new row in a Google Sheet tab',
  },
  {
    key: 'notion_page_added',
    category: 'When something changes',
    icon: iconDatabase,
    iconColor: CHANGE,
    name: 'A page is added to a Notion database',
    description: 'Run on new pages in a Notion database',
  },
  {
    key: 'notion_page_updated',
    category: 'When something changes',
    icon: iconDatabase,
    iconColor: CHANGE,
    name: 'A page in a Notion database is updated',
    description: 'Run each time a page in a Notion database is edited',
  },
  {
    key: 'google_doc_updated',
    category: 'When something changes',
    icon: iconFileLines,
    iconColor: CHANGE,
    name: 'A picked Google Doc is updated',
    description: 'Run when a Google Doc you pick is edited',
  },
  {
    key: 'job_change',
    category: 'When something changes',
    icon: iconBriefcase,
    iconColor: CHANGE,
    name: 'Someone changes jobs',
    description: 'A contact you already track turns up at a new company',
  },
  {
    key: 'new_people',
    category: 'On a schedule',
    icon: iconUserGroup,
    iconColor: SCHEDULE,
    name: 'New people match your ICP',
    description: 'Pick up people who newly match a search, every week',
  },
  {
    key: 'new_companies',
    category: 'On a schedule',
    icon: iconBuildings,
    iconColor: SCHEDULE,
    name: 'New companies match your ICP',
    description: 'Pick up companies that newly match a search, every week',
  },
  {
    key: 'new_jobs',
    category: 'On a schedule',
    icon: iconBriefcase,
    iconColor: SCHEDULE,
    name: 'Companies start hiring',
    description: 'Find companies with new job posts that match your filters',
  },
  {
    key: 'tool_adopted',
    category: 'On a schedule',
    icon: iconBuildings,
    iconColor: SCHEDULE,
    name: 'Companies start using a tool',
    description: 'Watch new companies using a technology who match your ICP',
  },
  {
    key: 'company_signal',
    category: 'On a schedule',
    icon: iconBuildings,
    iconColor: SCHEDULE,
    name: 'Something happens at an account',
    description: 'Watch hiring, news, funding and headcount at named accounts',
  },
  {
    key: 'post_engagers',
    category: 'On a schedule',
    icon: iconUserGroup,
    iconColor: SCHEDULE,
    name: 'People engage with posts',
    description: 'Find indexed engagers who match your ICP',
  },
  {
    key: 'crm_duplicates',
    category: 'On a schedule',
    icon: iconCopy,
    iconColor: SCHEDULE,
    name: 'CRM duplicates',
    description: 'Find duplicate Salesforce records, every quarter',
  },
];

/** Kinds offered only on an agent's play (W5: CRM cleanup). */
export const AGENT_ONLY_TRIGGER_KINDS: readonly TriggerKind[] = [
  'deal_stage',
  'deal_stalled',
  'sixsense_stage',
  'meeting_booked',
  'meeting_starting',
  'meeting_no_notes',
  'crm_duplicates',
  'fireflies_call',
  'fathom_call',
  'gong_call',
  'new_jobs',
  'post_engagers',
  'tool_adopted',
  'company_signal',
  'list_leads_added',
  'sheet_rows_added',
  'google_doc_updated',
  'webhook',
  'play_handoff',
  'sequence_event',
  'job_change',
  'notion_page_added',
  'notion_page_updated',
];

/**
 * Kinds without a supported engine reader cannot be offered for new plays.
 */
const UNSUPPORTED_TRIGGER_KINDS: readonly TriggerKind[] = [];

/** The rows a play's library outside Agents offers and filters by. */
export const LIBRARY_TRIGGER_ROWS: TriggerRow[] = TRIGGER_ROWS.filter(
  (row) =>
    !AGENT_ONLY_TRIGGER_KINDS.includes(row.key) &&
    !UNSUPPORTED_TRIGGER_KINDS.includes(row.key)
);

/**
 * Whether a trigger picker offers a kind. CRM duplicates shows only while
 * `cleanupOffered`: `useCleanupSeed` has a seed, so it is an agent's play
 * (Agents unlocked, hidden while it loads), a CRM a cleanup may run on is
 * connected (Salesforce or HubSpot), and that CRM's
 * settings are read. Job change requires current Agent and CRM access. A play
 * that already holds a kind keeps it, so the saved
 * value still reads. The builder's pickers and the agent's app events both
 * filter by this.
 */
export function offersTriggerKind(
  kind: TriggerKind,
  {
    cleanupOffered,
    agentPlay = false,
    listOffered = false,
    webhookOffered = false,
    handoffOffered = false,
    firefliesOffered = false,
    fathomOffered = false,
    gongOffered = false,
    jobChangeOffered = false,
    notionOffered = false,
    sixsenseOffered = false,
    current,
  }: {
    cleanupOffered: boolean;
    agentPlay?: boolean;
    listOffered?: boolean;
    webhookOffered?: boolean;
    handoffOffered?: boolean;
    firefliesOffered?: boolean;
    fathomOffered?: boolean;
    gongOffered?: boolean;
    jobChangeOffered?: boolean;
    /** Agents unlocked (false while it loads). */
    notionOffered?: boolean;
    /** sixsense_v1, read as `=== true` (false while it loads). */
    sixsenseOffered?: boolean;
    current?: TriggerKind;
  }
): boolean {
  if (isNotionTriggerKind(kind)) return agentPlay && notionOffered;
  if (kind === 'deal_stage' || kind === 'deal_stalled')
    return agentPlay && listOffered;
  if (kind === 'sixsense_stage')
    return agentPlay && listOffered && sixsenseOffered;
  // Each call tool is its own trigger, offered on an agent's play,
  // connected or not: a play saves without the connection.
  if (kind === 'fireflies_call') return agentPlay && firefliesOffered;
  if (kind === 'fathom_call') return agentPlay && fathomOffered;
  if (kind === 'gong_call') return agentPlay && gongOffered;
  if (isCalendarTriggerKind(kind)) return agentPlay && listOffered;
  if (kind === 'sequence_event') return false;
  if (kind === 'job_change') return agentPlay && jobChangeOffered;
  if (kind === 'new_jobs') return agentPlay;
  if (kind === 'tool_adopted') return agentPlay && listOffered;
  if (kind === 'company_signal') return agentPlay && listOffered;
  if (kind === 'post_engagers') return agentPlay && listOffered;
  if (kind === 'sheet_rows_added') return agentPlay && listOffered;
  if (kind === 'google_doc_updated') return agentPlay && listOffered;
  if (kind === current) return true;
  if (kind === 'list_leads_added') return agentPlay && listOffered;
  if (kind === 'webhook') return agentPlay && webhookOffered;
  if (kind === 'play_handoff') return agentPlay && handoffOffered;
  if (UNSUPPORTED_TRIGGER_KINDS.includes(kind)) return false;
  return kind !== 'crm_duplicates' || cleanupOffered;
}

/** The rows the trigger picker offers for this play (`offersTriggerKind`). */
export function triggerRowsFor(options: {
  cleanupOffered: boolean;
  agentPlay?: boolean;
  listOffered?: boolean;
  webhookOffered?: boolean;
  handoffOffered?: boolean;
  firefliesOffered?: boolean;
  fathomOffered?: boolean;
  gongOffered?: boolean;
  jobChangeOffered?: boolean;
  notionOffered?: boolean;
  sixsenseOffered?: boolean;
  current?: TriggerKind;
}): TriggerRow[] {
  return TRIGGER_ROWS.filter((row) => offersTriggerKind(row.key, options));
}

/* ── The sectioned trigger dialog ───────────────────────────────────────── */

/**
 * The trigger dialog lists its rows by the tool they read, and a CRM's rows and
 * the webhook formats each get a row of their own. The rows are a display layer
 * over TRIGGER_ROWS: a row resolves to the same kind, with the CRM or the
 * webhook format it sets, so the stored config is today's and TRIGGER_ROWS
 * stays one row per kind (the Plays tab's filter counts it).
 */

export type TriggerSection =
  | 'cleanlist'
  | 'salesforce'
  | 'hubspot'
  | 'attio'
  | 'sequencing'
  | 'meetings'
  | 'docs'
  | 'lists'
  | 'website'
  | 'developer'
  | 'plays';

export const TRIGGER_SECTIONS: readonly {
  id: TriggerSection;
  label: string;
}[] = [
  { id: 'cleanlist', label: `${BRAND.name} data` },
  { id: 'salesforce', label: 'Salesforce' },
  { id: 'hubspot', label: 'HubSpot' },
  { id: 'attio', label: 'Attio' },
  { id: 'sequencing', label: 'Sequencing' },
  { id: 'meetings', label: 'Meetings' },
  { id: 'docs', label: 'Docs and notes' },
  { id: 'lists', label: 'Lists' },
  { id: 'website', label: 'Website' },
  { id: 'developer', label: 'Developer' },
  { id: 'plays', label: 'Other plays' },
];

export type WebhookPreset = NonNullable<IncomingWebhookConfig['preset']>;
type TriggerCrm = PlayCrm;

export interface TriggerConnectorRow {
  /** Unique: `salesforce:deal_stage`, `webhook:rb2b_visitor`, else the kind. */
  id: string;
  section: TriggerSection;
  kind: TriggerKind;
  name: string;
  /** The CRM a CRM row sets. */
  crmType?: TriggerCrm;
  /** The webhook format a webhook row sets. */
  preset?: WebhookPreset;
  /** The tool whose logo the row shows; null keeps the kind's glyph. */
  tool: ConnectorId | null;
  connection: ConnectionSource | null;
  /** The rows the trigger panel's "Start this play when" lists together:
   *  one tool's. */
  group: string;
  keywords: readonly string[];
  icon: IconDefinition;
  iconColor: string;
  /** What it watches in a short phrase (under 45 characters, no dashes):
   *  the dialog card's line where no connection state applies. */
  description: string;
}

type RowSpec = Omit<
  TriggerConnectorRow,
  'name' | 'icon' | 'iconColor' | 'keywords' | 'description'
> & {
  /** Absent: the kind's own TRIGGER_ROWS name. */
  name?: string;
  /** Absent: the kind's CARD_LINES phrase. */
  description?: string;
  icon?: IconDefinition;
  keywords?: readonly string[];
};

/** A dialog card's line for each kind, short enough for one line. */
const CARD_LINES: Partial<Record<TriggerKind, string>> = {
  new_people: 'People who newly match a search',
  new_companies: 'Companies that newly match a search',
  new_jobs: 'New job posts that match your filters',
  tool_adopted: 'Companies newly using a technology',
  company_signal: 'Hiring, news and funding at accounts',
  post_engagers: 'Engagers who match your ICP',
  crm_property_change: 'A record starts matching your conditions',
  deal_stage: 'A deal enters a stage you choose',
  deal_stalled: 'An open deal has no activity for days',
  sixsense_stage: 'A Salesforce account reaches a 6sense stage',
  job_change: 'A tracked contact moves to a new company',
  crm_duplicates: 'Find duplicate records every quarter',
  sequence_event: 'Start from people in a sequence',
  meeting_booked: 'Outside attendees of a new meeting',
  meeting_starting: 'Outside attendees before it starts',
  meeting_no_notes: 'No notes two hours after it ends',
  fireflies_call: 'Outside participants once notes sync',
  fathom_call: 'Outside participants once notes sync',
  gong_call: 'Outside participants once notes sync',
  sheet_rows_added: 'Each new row in a sheet tab',
  google_doc_updated: 'Each edit to a doc you pick',
  notion_page_added: 'New pages in a database',
  notion_page_updated: 'Edited pages in a database',
  list_leads_added: `Run on new leads in a ${BRAND.name} list`,
  webhook: 'A form or JSON post from another app',
  play_handoff: 'People or companies from another play',
};

const CRM_KINDS = [
  'crm_property_change',
  'crm_list_membership',
  'deal_stage',
  'deal_stalled',
  'job_change',
  'crm_duplicates',
] as const satisfies readonly TriggerKind[];
type CrmKind = (typeof CRM_KINDS)[number];

function isCrmKind(kind: TriggerKind): kind is CrmKind {
  return (CRM_KINDS as readonly TriggerKind[]).includes(kind);
}

/** The CRM kinds each CRM's section offers. Attio has no job change (it
 *  needs the CRM identity index) and no duplicates (cleanup reads Salesforce
 *  and HubSpot only), attio-build-plan D16. */
const CRM_KINDS_BY_CRM: Record<TriggerCrm, readonly CrmKind[]> = {
  salesforce: CRM_KINDS,
  hubspot: CRM_KINDS,
  attio: [
    'crm_property_change',
    'crm_list_membership',
    'deal_stage',
    'deal_stalled',
  ],
};

const LIST_ROW_WORDS: Record<TriggerCrm, { name: string; line: string }> = {
  salesforce: {
    name: 'A record joins a campaign',
    line: 'Someone is added to a campaign',
  },
  hubspot: {
    name: 'A record joins a list',
    line: 'Someone is added to a HubSpot list',
  },
  attio: {
    name: 'A record joins a list',
    line: 'Someone is added to an Attio list',
  },
};

function crmSpecs(crm: TriggerCrm): RowSpec[] {
  const own = { section: crm, crmType: crm, tool: crm, group: crm } as const;
  const connection = 'crm' as const;
  const names: Record<CrmKind, string> = {
    crm_property_change: 'A record matches',
    crm_list_membership: LIST_ROW_WORDS[crm].name,
    deal_stage: 'A deal changes stage',
    deal_stalled: 'A deal stalls',
    job_change: 'A contact changes jobs',
    crm_duplicates: 'Duplicates',
  };
  const keywords: Record<CrmKind, readonly string[]> = {
    crm_property_change: ['field', 'property', 'conditions'],
    crm_list_membership: ['list', 'campaign', 'report', 'view'],
    deal_stage: ['opportunity', 'pipeline'],
    deal_stalled: ['opportunity', 'pipeline', 'inactive'],
    job_change: ['champion', 'moves'],
    crm_duplicates: ['dedupe', 'cleanup'],
  };
  return CRM_KINDS_BY_CRM[crm].map((kind) => ({
    ...own,
    id: `${crm}:${kind}`,
    kind,
    name: names[kind],
    ...(kind === 'crm_list_membership'
      ? { description: LIST_ROW_WORDS[crm].line }
      : {}),
    connection,
    keywords: ['crm', ...keywords[kind]],
  }));
}

function cleanlistSpec(kind: TriggerKind): RowSpec {
  return {
    id: kind,
    section: 'cleanlist',
    kind,
    tool: null,
    connection: null,
    group: 'cleanlist',
    keywords: ['icp', 'search'],
  };
}

function webhookSpec(
  preset: WebhookPreset,
  section: TriggerSection,
  name: string,
  description: string,
  group: string,
  icon: IconDefinition,
  keywords: readonly string[]
): RowSpec {
  return {
    id: `webhook:${preset}`,
    section,
    kind: 'webhook',
    name,
    description,
    preset,
    tool: null,
    connection: null,
    group,
    icon,
    keywords: ['webhook', ...keywords],
  };
}

const workspace = 'workspace' as const;

/** Every row the sectioned dialog can show, in section order. */
const TRIGGER_CONNECTOR_SPECS: readonly RowSpec[] = [
  cleanlistSpec('new_people'),
  cleanlistSpec('new_companies'),
  cleanlistSpec('new_jobs'),
  cleanlistSpec('tool_adopted'),
  cleanlistSpec('company_signal'),
  cleanlistSpec('post_engagers'),
  ...crmSpecs('salesforce'),
  {
    id: 'sixsense_stage',
    section: 'salesforce',
    kind: 'sixsense_stage',
    crmType: 'salesforce',
    tool: 'salesforce',
    connection: 'crm',
    group: 'salesforce',
    keywords: ['crm', '6sense', 'buying stage', 'account'],
  },
  ...crmSpecs('hubspot'),
  ...crmSpecs('attio'),
  {
    id: 'sequence_event',
    section: 'sequencing',
    kind: 'sequence_event',
    name: `Someone replies or finishes a ${BRAND.name} sequence`,
    tool: 'cleanlist',
    connection: null,
    group: 'sequence_event',
    keywords: ['reply', 'finished'],
  },
  ...(['meeting_booked', 'meeting_starting', 'meeting_no_notes'] as const).map(
    (kind): RowSpec => ({
      id: kind,
      section: 'meetings',
      kind,
      tool: 'google_calendar',
      connection: workspace,
      group: 'google_calendar',
      keywords: ['calendar', 'meeting'],
    })
  ),
  {
    id: 'fireflies_call',
    section: 'meetings',
    kind: 'fireflies_call',
    tool: 'fireflies',
    connection: workspace,
    group: 'fireflies',
    keywords: ['call', 'transcript'],
  },
  {
    id: 'fathom_call',
    section: 'meetings',
    kind: 'fathom_call',
    tool: 'fathom',
    connection: workspace,
    group: 'fathom',
    keywords: ['call', 'transcript'],
  },
  {
    id: 'gong_call',
    section: 'meetings',
    kind: 'gong_call',
    tool: 'gong',
    connection: workspace,
    group: 'gong',
    keywords: ['call', 'transcript'],
  },
  webhookSpec(
    'calendly_booked',
    'meetings',
    'A Calendly meeting is booked',
    'Invitees of a new Calendly booking',
    'calendly',
    iconCalendarDays,
    ['calendly', 'meeting']
  ),
  webhookSpec(
    'calendly_no_show',
    'meetings',
    'A Calendly invitee is a no-show',
    'Invitees who miss a Calendly meeting',
    'calendly',
    iconCalendarDays,
    ['calendly', 'meeting', 'missed']
  ),
  {
    id: 'sheet_rows_added',
    section: 'docs',
    kind: 'sheet_rows_added',
    tool: 'google_sheets',
    connection: workspace,
    group: 'google_sheets',
    keywords: ['spreadsheet', 'rows'],
  },
  {
    id: 'google_doc_updated',
    section: 'docs',
    kind: 'google_doc_updated',
    tool: 'google_docs',
    connection: workspace,
    group: 'google_docs',
    keywords: ['document'],
  },
  ...(['notion_page_added', 'notion_page_updated'] as const).map(
    (kind): RowSpec => ({
      id: kind,
      section: 'docs',
      kind,
      tool: 'notion',
      connection: workspace,
      group: 'notion',
      keywords: ['database', 'page'],
    })
  ),
  {
    id: 'list_leads_added',
    section: 'lists',
    kind: 'list_leads_added',
    tool: null,
    connection: null,
    group: 'list_leads_added',
    keywords: ['leads'],
  },
  webhookSpec(
    'rb2b_visitor',
    'website',
    'RB2B identifies a site visitor',
    'Named visitors to your website',
    'webhook:rb2b_visitor',
    iconGlobe,
    ['rb2b', 'visitor', 'website']
  ),
  {
    id: 'webhook',
    section: 'developer',
    kind: 'webhook',
    tool: null,
    connection: null,
    group: 'webhook',
    icon: iconWebhook,
    keywords: ['http', 'api', 'json', 'form'],
  },
  webhookSpec(
    'segment_track',
    'developer',
    'A Segment event arrives',
    'A Segment Track call for a person',
    'webhook:segment_track',
    iconWebhook,
    ['segment', 'track', 'event']
  ),
  {
    id: 'play_handoff',
    section: 'plays',
    kind: 'play_handoff',
    tool: null,
    connection: null,
    group: 'play_handoff',
    keywords: ['handoff', 'play'],
  },
];

/** The row a dialog pick reads as: its kind, with its CRM or its webhook
 *  format. A CRM kind with no CRM is a legacy Salesforce trigger; any other
 *  CRM keeps its own name, so it never reads as Salesforce's row. */
export function triggerChoiceRowId({
  kind,
  crmType,
  preset,
}: {
  kind: TriggerKind;
  crmType?: TriggerCrm;
  preset?: WebhookPreset;
}): string {
  if (isCrmKind(kind)) return `${crmType ?? 'salesforce'}:${kind}`;
  if (kind === 'webhook') return preset ? `webhook:${preset}` : 'webhook';
  return kind;
}

/** The row a saved trigger reads as. Null before a kind is picked. */
export function triggerRowIdOf(
  config: Partial<TriggerConfig> | undefined
): string | null {
  const kind = triggerKind(config);
  if (!kind) return null;
  const crmType =
    (kind === 'job_change' ? config?.jobChange?.crmType : undefined) ??
    config?.crm?.crmType;
  return triggerChoiceRowId({
    kind,
    crmType,
    preset: config?.webhook?.preset,
  });
}

/** Whether two rows are one tool's, the rows the trigger panel's "Start
 *  this play when" lists together. */
export function sameTriggerTool(a: string | null, b: string | null): boolean {
  const group = (id: string | null) =>
    TRIGGER_CONNECTOR_SPECS.find((spec) => spec.id === id)?.group;
  const own = group(a);
  return own !== undefined && own === group(b);
}

/** The mark a trigger row or card shows: its tool's logo, Cleanlist's for
 *  Cleanlist's own sources, none for a webhook or a handoff. */
export function triggerRowLogo(
  spec: Pick<RowSpec, 'tool' | 'section'> | undefined
): ConnectorId | null {
  if (!spec) return null;
  return spec.tool ?? (spec.section === 'cleanlist' ? 'cleanlist' : null);
}

/** A saved trigger's mark (`triggerRowLogo` of the row it reads as). */
export function triggerLogoOf(
  config: Partial<TriggerConfig> | undefined
): ConnectorId | null {
  const id = triggerRowIdOf(config);
  return triggerRowLogo(TRIGGER_CONNECTOR_SPECS.find((spec) => spec.id === id));
}

/**
 * The sectioned dialog's rows for the kinds a play offers
 * (`triggerRowsFor`): a CRM's rows for each CRM, Duplicates only on the CRM
 * the cleanup seed picked (as the Agents Triggers tab), the webhook formats
 * beside the webhook. `keep` is a row shown whatever the seed, the one a
 * saved trigger already reads as. Attio's rows show only while `/crm/options`
 * lists Attio (`attio`), or while a saved trigger reads as one of them.
 */
export function triggerConnectorRows(
  offered: readonly TriggerRow[],
  {
    cleanupCrm,
    keep,
    attio = false,
  }: {
    cleanupCrm?: 'salesforce' | 'hubspot';
    keep?: string | null;
    attio?: boolean;
  } = {}
): TriggerConnectorRow[] {
  const byKind = new Map(offered.map((row) => [row.key, row]));
  const keepAttio =
    TRIGGER_CONNECTOR_SPECS.find((spec) => spec.id === keep)?.section ===
    'attio';
  return TRIGGER_CONNECTOR_SPECS.flatMap((spec): TriggerConnectorRow[] => {
    const row = byKind.get(spec.kind);
    if (!row) return [];
    if (spec.section === 'attio' && !attio && !keepAttio) return [];
    if (
      spec.kind === 'crm_duplicates' &&
      spec.crmType !== cleanupCrm &&
      spec.id !== keep
    )
      return [];
    return [
      {
        ...spec,
        name: spec.name ?? row.name,
        description:
          spec.description ?? CARD_LINES[spec.kind] ?? row.description,
        icon: spec.icon ?? row.icon,
        iconColor: row.iconColor,
        keywords: spec.keywords ?? [],
      },
    ];
  });
}

/** Rows under their sections, in section order, leaving out an empty one. */
export function triggerConnectorSections(
  rows: readonly TriggerConnectorRow[]
): { id: TriggerSection; label: string; rows: TriggerConnectorRow[] }[] {
  return TRIGGER_SECTIONS.map((section) => ({
    ...section,
    rows: rows.filter((row) => row.section === section.id),
  })).filter((section) => section.rows.length > 0);
}

/** The words the dialog's search matches a row on: its section, name and
 *  keywords. */
export function triggerRowSearchValue(row: TriggerConnectorRow): string {
  const section = TRIGGER_SECTIONS.find((s) => s.id === row.section);
  return [section?.label, row.name, ...row.keywords].join(' ');
}
